'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Users, Star, Award } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useHasMounted } from '@/hooks/use-has-mounted';
import { type PartnerTeam } from '@impact/types';

function formatUsd(amount: number): string {
  return `$${Math.round(amount).toLocaleString()}`;
}

function TeamInitials({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map((token) => token[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-stone-500 bg-stone-800">
      {initials}
    </div>
  );
}

function TeamCard({ team, mounted }: { team: PartnerTeam; mounted: boolean }) {
  const isFeatured = team.teamLevel === 'featured';
  const imageSrc = team.logoUrl ?? team.eventThumbnailUrl ?? null;

  return (
    <Link
      href={team.handle ? `/team/${team.handle}` : '#'}
      className="group relative flex flex-col bg-stone-900 rounded-2xl border border-stone-800 overflow-hidden hover:border-uwr-orange/40 hover:bg-stone-800/60 transition-all duration-300"
    >
      {/* Featured badge */}
      {isFeatured && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1 px-2 py-1 rounded-full bg-uwr-orange text-white text-xs font-semibold">
          <Star className="w-3 h-3 fill-current" />
          Featured
        </div>
      )}
      {!isFeatured && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1 px-2 py-1 rounded-full bg-stone-700/80 text-stone-300 text-xs font-medium border border-stone-600">
          <Award className="w-3 h-3" />
          Partner
        </div>
      )}

      {/* Team image */}
      <div className="relative w-full aspect-square bg-stone-800 overflow-hidden">
        {imageSrc ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imageSrc}
              alt={team.name}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </>
        ) : (
          <TeamInitials name={team.name} />
        )}
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-transparent to-transparent" />
      </div>

      {/* Card body */}
      <div className="flex flex-col flex-1 p-5">
        <div className="mb-3">
          <h3 className="text-white font-semibold text-base truncate leading-tight group-hover:text-uwr-orange transition-colors">
            {team.name}
          </h3>
          {team.handle && <p className="text-stone-500 text-xs mt-0.5 truncate">@{team.handle}</p>}
          {team.memberCount > 0 && (
            <p className="text-stone-500 text-xs mt-1">
              {team.memberCount} {team.memberCount === 1 ? 'rider' : 'riders'}
            </p>
          )}
        </div>

        <div className="mt-auto pt-3 border-t border-stone-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-stone-400 text-xs">
              <Users className="w-3.5 h-3.5 text-uwr-orange" />
              <span>View Team</span>
            </div>
            {mounted && team.totalRaisedUsd > 0 && (
              <div className="text-right">
                <p className="text-uwr-orange font-bold text-sm">
                  {formatUsd(team.totalRaisedUsd)}
                </p>
                <p className="text-stone-500 text-[10px]">raised</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

function PartnerTeamsSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="bg-stone-900 rounded-2xl border border-stone-800 overflow-hidden animate-pulse"
        >
          <div className="aspect-square bg-stone-800" />
          <div className="p-5 space-y-3">
            <div className="h-4 bg-stone-800 rounded w-3/4" />
            <div className="h-3 bg-stone-800 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface PartnerTeamsPageProps {
  prefetchedData?: { teams: PartnerTeam[] };
}

export function PartnerTeamsPage({ prefetchedData }: PartnerTeamsPageProps) {
  const mounted = useHasMounted();
  const { data: teams = [], isLoading } = trpc.publicTeam.listPartners.useQuery(undefined, {
    ...(prefetchedData?.teams && { initialData: prefetchedData.teams }),
  });

  const featured = teams.filter((t) => t.teamLevel === 'featured');
  const partners = teams.filter((t) => t.teamLevel === 'partner');

  return (
    <main className="flex-1 bg-white pb-16">
      {/* Hero header */}
      <section className="relative h-[280px] md:h-[360px]">
        <Image
          src="/images/uwr/HeroImageUWR.jpg"
          alt="Partner Teams"
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />

        {/* Hero Content */}
        <div className="absolute inset-0 flex items-end">
          <div className="container max-w-6xl pb-10 md:pb-14">
            <h1 className="text-4xl md:text-5xl font-bold text-white">Partner Teams</h1>
            {mounted && teams.some((t) => t.totalRaisedUsd > 0) && (
              <div className="mt-6">
                <p className="text-2xl font-bold text-uwr-orange">
                  {formatUsd(teams.reduce((sum, t) => sum + t.totalRaisedUsd, 0))}
                </p>
                <p className="text-white/50 text-sm">Total Raised</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="container max-w-6xl py-12 space-y-14">
        {isLoading ? (
          <PartnerTeamsSkeleton />
        ) : teams.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-stone-400" />
            </div>
            <p className="text-stone-700 text-lg font-medium">No partner teams yet</p>
            <p className="text-stone-500 text-sm mt-1">
              Check back soon for our partner team announcements.
            </p>
          </div>
        ) : (
          <>
            {featured.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Star className="w-5 h-5 text-uwr-orange fill-current" />
                  <h2 className="text-xl font-semibold text-stone-900">Featured Teams</h2>
                  <span className="px-2 py-0.5 rounded-full bg-uwr-orange/15 text-uwr-orange text-xs font-medium">
                    {featured.length}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                  {featured.map((team) => (
                    <TeamCard key={team.id} team={team} mounted={mounted} />
                  ))}
                </div>
              </div>
            )}

            {partners.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Award className="w-5 h-5 text-stone-500" />
                  <h2 className="text-xl font-semibold text-stone-900">Partner Teams</h2>
                  <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 text-xs font-medium border border-stone-200">
                    {partners.length}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                  {partners.map((team) => (
                    <TeamCard key={team.id} team={team} mounted={mounted} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
